import { createContext, useContext } from 'react';
import { AxiosError } from 'axios';
import { RefetchFunction } from 'axios-hooks';
import { arrange, asc, groupBy, tidy } from '@tidyjs/tidy';

import {
  IInfoCategoryMasterData,
  useGetCountryMasterData,
  useGetInfoCategoryMasterData,
} from '@Domains/MasterData';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';

type IncomeStructureProviderProps = {
  children: React.ReactNode;
};

type IncomeStructureContextProps = {
  productIncome: Array<IInfoCategoryMasterData>;
  exportIncome: Array<IInfoCategoryMasterData>;
  otherIncome: Array<IInfoCategoryMasterData>;
  countries: Array<IInfoCategoryMasterData>;
  loading: boolean;
  error?: AxiosError | null;
  refetch?: RefetchFunction<unknown, Array<IInfoCategoryMasterData>>;
};

const IncomeStructureContext = createContext<IncomeStructureContextProps>({
  productIncome: [],
  exportIncome: [],
  otherIncome: [],
  countries: [],
  loading: false,
});

const IncomeStructureProvider = (props: IncomeStructureProviderProps) => {
  const { children } = props;

  const [{ data = [], loading, error }, refetch] = useGetInfoCategoryMasterData(
    [
      INFO_CATEGORY_MASTER_DATA.PRODUCT_INCOME,
      INFO_CATEGORY_MASTER_DATA.EXPORT_INCOME,
      INFO_CATEGORY_MASTER_DATA.OTHER_INCOME,
    ]
  );

  const [{ data: countryData = [] }] = useGetCountryMasterData(
    INFO_CATEGORY_MASTER_DATA.EXPORT_INCOME
  );

  const {
    PRODUCT_INCOME: productIncome,
    EXPORT_INCOME: exportIncome,
    OTHER_INCOME: otherIncome,
  } = tidy(
    data,
    arrange([asc('sequence')]),
    groupBy('infoCategory', groupBy.object())
  );

  const countries = tidy(countryData, arrange([asc('sequence')]));

  return (
    <IncomeStructureContext.Provider
      value={{
        productIncome,
        exportIncome,
        otherIncome,
        loading,
        error,
        refetch,
        countries,
      }}
    >
      {children}
    </IncomeStructureContext.Provider>
  );
};

const useIncomeStructure = () => {
  const context = useContext(IncomeStructureContext);

  if (!context) {
    throw new Error(
      'useIncomeStructure must be used within a IncomeStructureProvider'
    );
  }

  return context;
};

export { useIncomeStructure };
export default IncomeStructureProvider;
